import React, { useMemo, useState, useEffect } from "react";
import { usePaged, TablePager } from "@/components/dashboard/Pager";
import { useCollapse, CollapseToggle } from "@/components/dashboard/Collapse";
import api from "@/lib/api";
import { todayIn } from "@/lib/dates";
import { sinceChartStart, chartStart } from "@/lib/chartWindow";
import { estDailyYieldCa, stakingIncomeByDate, botStakingIncomeByDate, botStakedOnDate, buildStakingCurve } from "@/lib/staking";
import { Cpu, Coins, Users, Trophy, TrendingUp, Search, X, Server, Gauge, AlertTriangle, Layers } from "lucide-react";
import { AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Brush, ReferenceLine } from "recharts";

const usd = (n, dec = 0) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: dec })}`;
const ca2 = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 }));

function eachDay(start, end) {
  const out = []; const d = new Date(start + "T00:00:00Z"); const e = new Date(end + "T00:00:00Z");
  let guard = 0;
  while (d <= e && guard < 800) { out.push(d.toISOString().slice(0, 10)); d.setUTCDate(d.getUTCDate() + 1); guard++; }
  return out;
}

function MiniKPI({ label, value, sub, color, tid }) {
  return (
    <div className="kpi-tile !p-3" data-testid={tid}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-num" style={{ fontSize: 22, color }}>{value}</div>
      {sub && <div className="text-[11px] text-ink-3 mt-0.5">{sub}</div>}
    </div>
  );
}

function SourceChip({ imported }) {
  return imported
    ? <span className="chip text-[10px]" style={{ background: "rgba(0,134,58,0.08)", color: "#00863A" }}>CaryPact</span>
    : <span className="chip text-[10px]" style={{ background: "rgba(15,18,38,0.05)", color: "#8A8FA8" }}>Manual</span>;
}

const tooltipStyle = { border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.97)", fontSize: 12 };

export function MiningView({ entries, wallets }) {
  const sorted = useMemo(() => [...entries].sort((a, b) => a.date.localeCompare(b.date)), [entries]);
  const miningRows = useMemo(() => [...sorted].reverse(), [sorted]);
  const miningPager = usePaged(miningRows);
  const byDate = useMemo(() => {
    const m = {};
    sorted.forEach((e) => { m[e.date] = (m[e.date] || 0) + (e.usd_value || 0); });
    // Today is always a partial day — stop the chart at yesterday to avoid a fake drop-off.
    const today = todayIn();
    return Object.entries(m).map(([date, v]) => ({ date, usd: +v.toFixed(2) }))
      .filter((d) => d.date < today)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [sorted]);
  const total = sorted.reduce((a, e) => a + (e.usd_value || 0), 0);
  const days = byDate.length;
  const avg = days ? total / days : 0;
  const best = byDate.reduce((m, d) => (d.usd > (m?.usd || -1) ? d : m), null);
  // KPIs above count every day; the chart opens on 20 May (see lib/chartWindow).
  const chartData = useMemo(() => sinceChartStart(byDate), [byDate]);
  // A row read through someone else's showcase carries its own anonymous label —
  // the numbers are the leader's to share, the wallet names are not.
  const wLabel = (id, row) => row?.wallet_label || wallets.find((w) => w.id === id)?.label || "—";

  return (
    <div data-testid="mining-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        <MiniKPI label="Total CA mining revenue" value={usd(total)} color="#8D1CEB" tid="mining-kpi-total" />
        <MiniKPI label="Days logged" value={days} tid="mining-kpi-days" />
        <MiniKPI label="Avg / day" value={usd(avg, 2)} tid="mining-kpi-avg" />
        <MiniKPI label={best ? `Best · ${best.date}` : "Best day"} value={best ? usd(best.usd, 2) : "—"} color="#B45309" tid="mining-kpi-best" />
      </div>
      <div className="glass p-5 mb-4" data-testid="mining-chart">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(141,28,235,0.12)" }}><Cpu size={16} style={{ color: "#8D1CEB" }} /></div>
          <h3 className="font-display text-lg font-semibold tracking-tight">Daily CA mining income</h3>
        </div>
        {chartData.length === 0 ? (
          <div className="text-sm text-ink-3 py-12 text-center" data-testid="mining-empty">No CA mining income yet — connect a wallet in "Update data" to import it from CaryPact.</div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [usd(v, 2), "CA mining $"]} cursor={{ fill: "rgba(141,28,235,0.05)" }} />
                <Bar dataKey="usd" name="CA mining $" fill="#8D1CEB" radius={[4, 4, 0, 0]} maxBarSize={16} />
                {chartData.length > 45 && <Brush dataKey="date" height={18} stroke="rgba(141,28,235,0.45)" travellerWidth={8} fill="rgba(141,28,235,0.04)" tickFormatter={() => ""} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="glass overflow-hidden">
        <div className="p-5 border-b border-black/5"><h3 className="font-display text-lg font-semibold tracking-tight">Recent CA mining entries</h3></div>
        <div className="overflow-x-auto">
          <table className="modern" data-testid="mining-table">
            <thead><tr><th>Date</th><th>Wallet</th><th>CA earned</th><th>CA price</th><th>USD</th><th>Source</th></tr></thead>
            <tbody>
              {sorted.length === 0 ? <tr><td colSpan="6" className="text-center py-8 text-ink-3">Nothing yet.</td></tr>
              : miningPager.pageItems.map((e) => (
                <tr key={e.id}>
                  <td className="num text-xs">{e.date}</td>
                  <td className="text-sm">{wLabel(e.wallet_id, e)}</td>
                  <td className="num">{(e.ca_earned || 0).toFixed(4)}</td>
                  <td className="num">{e.ca_price ? `$${e.ca_price.toFixed(2)}` : "—"}</td>
                  <td className="num font-semibold" style={{ color: "#00863A" }}>${(e.usd_value || 0).toFixed(2)}</td>
                  <td><SourceChip imported={e.imported} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager pager={miningPager} testId="mining-pager" label="entries" />
      </div>
    </div>
  );
}

const BOT_GREEN = "#15DCAC";
const HASH_BLUE = "#2563EB";
const STAKE_LEVEL = "#F59E0B";
const bot4 = (n) => (n == null ? "—" : Number(n).toLocaleString(undefined, { maximumFractionDigits: 4 }));

// CaryPact returns some miner levels as Chinese names (e.g. 普通用户 = no VIP level).
// Normalise to the English/V-code label the rest of the app uses.
const CN_BOT_LEVELS = { "普通用户": "Standard", "普通会员": "Standard", "普通": "Standard" };
export const botLevelLabel = (v) => {
  const s = String(v == null ? "" : v).trim();
  if (!s) return null;
  if (CN_BOT_LEVELS[s]) return CN_BOT_LEVELS[s];
  if (/^v\d+$/i.test(s)) return s.toUpperCase();
  if (/[\u4e00-\u9fff]/.test(s)) {
    const m = s.match(/\d+/);
    return m ? `V${m[0]}` : "Standard";
  }
  return s;
};

export function BotMiningView({ totals, wallets, botPrice, daily, rewardSeries }) {
  const t = totals || {};
  // One chart, one currency: the BOT earnings tile is a dollar figure made of four
  // parts, so the daily chart is in dollars too. bothash_* (CaryPact's botB account)
  // is the VIP level dividend — already inside bot_usd, so it's stacked as a
  // breakdown (static mining = total − dividend) and never added on top. Server
  // sales and BOT power referral settle in USDT and come from their own screens.
  const botDaily = useMemo(() => {
    const rs = {};
    (rewardSeries || []).forEach((r) => {
      rs[r.date] = { sales: r.server_sales_usd || 0, referral: r.bot_power_referral_usd || 0,
                     hashrate: r.bot_power_mining_usd || 0, stakeLevel: r.bot_stake_level_usd || 0 };
    });
    const byDay = {};
    (daily || []).forEach((d) => { byDay[d.date] = d; });
    const dates = [...new Set([...Object.keys(byDay), ...Object.keys(rs)])].sort();
    const rows = dates.map((date) => {
      const d = byDay[date] || {};
      const total = Number(d.bot_usd || 0);
      const dividend = Math.min(total, Math.max(0, Number(d.bothash_usd || 0)));
      const x = rs[date] || {};
      return {
        date,
        mining: +(total - dividend).toFixed(2),
        dividend: +dividend.toFixed(2),
        hashrate: +Number(x.hashrate || 0).toFixed(2),
        sales: +Number(x.sales || 0).toFixed(2),
        referral: +Number(x.referral || 0).toFixed(2),
        stakeLevel: +Number(x.stakeLevel || 0).toFixed(2),
      };
    });
    return sinceChartStart(rows).filter((d) => d.mining + d.dividend + d.hashrate + d.sales + d.referral + d.stakeLevel > 0);
  }, [daily, rewardSeries]);

  // Money CaryPact reports as a running total with no dates can't be put on a day,
  // so it stays in the tile and gets said out loud here instead of being smeared
  // across the calendar (which is what made August look like it earned BOT money).
  const undatedUsd = useMemo(() => {
    const rs = t.reward_streams || {};
    return ["server_sales", "bot_power_referral", "bot_power_mining", "bot_stake_level"].reduce((a, k) => a + Number(rs[k]?.undated_usd || 0), 0);
  }, [t]);
  const rows = useMemo(() => {
    const list = (wallets || []).filter(
      (w) => (w.bot_reward || 0) > 0 || (w.bot_reward_total || 0) > 0 ||
             (w.bot_static_total || 0) > 0 || (w.bot_dividend_total || 0) > 0 || (w.bot_withdrawn || 0) > 0 ||
             (w.bot_power_hashrate || 0) > 0 || (w.bot_power_total || 0) > 0 || (w.bot_power_reward || 0) > 0,
    );
    return list.sort((a, b) => (b.bot_reward_total || 0) - (a.bot_reward_total || 0));
  }, [wallets]);
  const avail = t.bot_reward || 0;
  const availUsd = botPrice ? avail * botPrice : null;
  const totalUsd = botPrice ? (t.bot_reward_total || 0) * botPrice : null;
  // Purchased BOT hashrate — CaryPact's own screens, its own money.
  const hashHeld = t.bot_power_hashrate || 0;
  const hashAvail = t.bot_power_reward || 0;
  const hashTotal = t.bot_power_total || 0;
  const hashSettling = t.bot_power_settling || 0;
  const lastRefresh = useMemo(() => {
    const stamps = rows.map((w) => w.cp_synced_at).filter(Boolean).sort();
    return stamps.length ? String(stamps[stamps.length - 1]).slice(0, 10) : null;
  }, [rows]);
  // Same reason as the big ledger: a community shouldn't scroll past 150 wallets.
  const { open: botRowsOpen, toggle: toggleBotRows } = useCollapse("botmining-by-wallet", rows.length);
  const botPager = usePaged(rows, 25);
  // BOT Staking Level Income, straight off its own CaryPact screen — including the
  // V-level it was paid at, which the records themselves carry.
  const stakeLevel = useMemo(() => {
    const r = (t.reward_streams || {}).bot_stake_level || {};
    return {
      amount: Number(r.amount || 0), usd: Number(r.usd || 0),
      available: Number(r.available || 0), withdrawn: Number(r.withdrawn || 0),
      records: Number(r.records || 0), readable: !!r.readable,
      level: r.level || null,
    };
  }, [t]);

  return (
    <div data-testid="botmining-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        <MiniKPI label="Available BOT Award in CaryPack" value={`${bot4(avail)} BOT`} sub={availUsd != null ? usd(availUsd, 2) : "withdrawable now"} color={BOT_GREEN} tid="botmining-kpi-available" />
        <MiniKPI label="Total BOT earned" value={`${bot4(t.bot_reward_total || 0)} BOT`} sub={totalUsd != null ? `${usd(totalUsd, 2)} lifetime` : "lifetime income"} color={BOT_GREEN} tid="botmining-kpi-total" />
        <MiniKPI label="Static mining reward" value={`${bot4(t.bot_static_total || 0)} BOT`} sub="daily BOT mining" tid="botmining-kpi-static" />
        <MiniKPI label="Level dividend" value={`${bot4(t.bot_dividend_total || 0)} BOT`} sub="VIP level bonus" color="#B45309" tid="botmining-kpi-dividend" />
      </div>
      <div className="glass p-5 mb-4" data-testid="botmining-summary">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(21,220,172,0.14)" }}><Server size={16} style={{ color: BOT_GREEN }} /></div>
          <div>
            <h3 className="font-display text-lg font-semibold tracking-tight">BOT Chain mining · income</h3>
            <div className="text-[11px] text-ink-3 mt-0.5">Live from CaryPact BOT mining — static mining reward + VIP level dividend, paid in BOT.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5">
          <MiniKPI label="Withdrawn BOT" value={`${bot4(t.bot_withdrawn || 0)} BOT`} sub="already paid out" tid="botmining-kpi-withdrawn" />
          <MiniKPI label="Available (USD)" value={availUsd != null ? usd(availUsd, 2) : "—"} sub={botPrice ? `${bot4(avail)} BOT × $${Number(botPrice).toFixed(4)}/BOT = ${usd(availUsd, 2)}` : "BOT price unavailable"} color={BOT_GREEN} tid="botmining-kpi-available-usd" />
          <MiniKPI label="Wallets earning BOT" value={rows.length} sub="in this portfolio" tid="botmining-kpi-wallets" />
        </div>
      </div>
      <div className="glass p-5 mb-4" data-testid="bothash-summary">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(37,99,235,0.14)" }}><Gauge size={16} style={{ color: HASH_BLUE }} /></div>
          <div>
            <h3 className="font-display text-lg font-semibold tracking-tight">BOT Hashrate Mining · purchased hashrate</h3>
            <div className="text-[11px] text-ink-3 mt-0.5">Hashrate bought with USDT, paying BOT every day on CaryPact&apos;s own hashrate screen. This is <strong>separate money</strong> from static mining and the level dividend above — it is added to the BOT total, not folded into it.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <MiniKPI label="Hashrate held" value={hashHeld ? Number(hashHeld).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"} sub="from CaryPact's Hashrate Center" color={HASH_BLUE} tid="bothash-kpi-hashrate" />
          <MiniKPI label="Withdrawable now" value={`${bot4(hashAvail)} BOT`} sub={botPrice ? usd(hashAvail * botPrice, 2) : "withdrawable now"} color={HASH_BLUE} tid="bothash-kpi-available" />
          <MiniKPI label="Earned from hashrate" value={`${bot4(hashTotal)} BOT`} sub={botPrice ? `${usd(hashTotal * botPrice, 2)} lifetime · on top of the BOT total` : "lifetime"} color={HASH_BLUE} tid="bothash-kpi-total" />
          <MiniKPI label="Still settling" value={`${bot4(hashSettling)} BOT`} sub="today's run, not yet paid out" tid="bothash-kpi-settling" />
        </div>
      </div>
      <div className="glass p-5 mb-4" data-testid="botstakelevel-summary">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(245,158,11,0.14)" }}><Layers size={16} style={{ color: STAKE_LEVEL }} /></div>
          <div>
            <h3 className="font-display text-lg font-semibold tracking-tight">BOT Staking Level Income</h3>
            <div className="text-[11px] text-ink-3 mt-0.5">CaryPact&apos;s newest Community Revenue screen — BOT paid on your own V-level against the BOT staked beneath you. <strong>Separate money</strong> from static mining, the level dividend and hashrate: it is added to the BOT total, not folded into it.</div>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
          <MiniKPI label="Total reward" value={`${bot4(stakeLevel.amount)} BOT`} sub={botPrice ? `${usd(stakeLevel.amount * botPrice, 2)} lifetime` : "lifetime"} color={STAKE_LEVEL} tid="botstakelevel-kpi-total" />
          <MiniKPI label="Total claimable" value={`${bot4(stakeLevel.available)} BOT`} sub={botPrice ? usd(stakeLevel.available * botPrice, 2) : "waiting to withdraw"} color={STAKE_LEVEL} tid="botstakelevel-kpi-claimable" />
          <MiniKPI label="Withdrawn" value={`${bot4(stakeLevel.withdrawn)} BOT`} sub="already taken out" tid="botstakelevel-kpi-withdrawn" />
          <MiniKPI label="Paid at level" value={stakeLevel.level || "—"} sub={`${stakeLevel.records} payout${stakeLevel.records === 1 ? "" : "s"} recorded`} tid="botstakelevel-kpi-level" />
        </div>
        {!stakeLevel.readable && (
          <div className="text-[11px] mt-2.5 flex items-start gap-1.5" style={{ color: "#B45309" }} data-testid="botstakelevel-unread">
            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
            <span>Not read yet — sync a wallet with a fresh CaryPact token and this fills in. We won&apos;t show a zero we haven&apos;t been told.</span>
          </div>
        )}
      </div>
      <div className="glass p-5 mb-4" data-testid="botmining-chart">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(21,220,172,0.14)" }}><Server size={16} style={{ color: BOT_GREEN }} /></div>
          <div>
            <h3 className="font-display text-lg font-semibold tracking-tight">Daily BOT earnings</h3>
            <div className="text-[11px] text-ink-3 mt-0.5" data-testid="botmining-chart-note">
              Every part of the tile, in dollars: static mining output + the VIP level dividend + BOT hashrate mining
              (bought hashrate, paid daily) + Computing Power Server Sales Reward + BOT Power Referral Reward
              + BOT Staking Level Income (paid on your V-level against the BOT staked beneath you).
              Mining is a running total from CaryPact, so each new reading is spread across the days it accrued over; hashrate mining and the two reward screens land on the exact days CaryPact itemised. First BOT payout: 2 Sep 2026.
              {lastRefresh ? ` Last refreshed ${lastRefresh} — sync a wallet with a fresh CaryPact token to extend the curve.` : ""}
            </div>
            {undatedUsd > 0.01 && (
              <div className="text-[11px] mt-1.5 flex items-start gap-1.5 max-w-3xl" style={{ color: "#B45309" }} data-testid="botmining-undated-note">
                <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                <span>
                  {usd(undatedUsd, 2)} of reward money is in the tile but not on this chart — our copy doesn&apos;t
                  have the day it landed on. Sync this wallet with a fresh CaryPact token and it will drop onto the
                  days CaryPact shows for it. We won&apos;t spread it across days it may not have been earned.
                </span>
              </div>
            )}
          </div>
        </div>
        {botDaily.length === 0 ? (
          <div className="text-sm text-ink-3 py-12 text-center" data-testid="botmining-chart-empty">No daily BOT mining income recorded yet — sync a wallet with a fresh CaryPact token and the curve starts building from that reading.</div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={botDaily} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [usd(v, 2), n]} labelFormatter={(l) => l} cursor={{ fill: "rgba(21,220,172,0.06)" }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="mining" name="Static mining" stackId="bot" fill={BOT_GREEN} radius={[0, 0, 0, 0]} maxBarSize={16} />
                <Bar dataKey="dividend" name="VIP level dividend" stackId="bot" fill="#B45309" radius={[0, 0, 0, 0]} maxBarSize={16} />
                <Bar dataKey="hashrate" name="BOT hashrate mining" stackId="bot" fill={HASH_BLUE} radius={[0, 0, 0, 0]} maxBarSize={16} />
                <Bar dataKey="sales" name="Computing power server sales" stackId="bot" fill="#DB2777" radius={[0, 0, 0, 0]} maxBarSize={16} />
                <Bar dataKey="referral" name="BOT power referral" stackId="bot" fill="#7C3AED" radius={[0, 0, 0, 0]} maxBarSize={16} />
                <Bar dataKey="stakeLevel" name="BOT staking level income" stackId="bot" fill="#F59E0B" radius={[4, 4, 0, 0]} maxBarSize={16} />
                {botDaily.length > 45 && <Brush dataKey="date" height={18} stroke="rgba(21,220,172,0.45)" travellerWidth={8} fill="rgba(21,220,172,0.04)" tickFormatter={() => ""} />}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="glass overflow-hidden">
        <div className="p-5 border-b border-black/5 flex items-center gap-3 flex-wrap">
          <h3 className="font-display text-lg font-semibold tracking-tight">BOT mining by wallet</h3>
          <span className="ml-auto flex items-center gap-2">
            <CollapseToggle open={botRowsOpen} onClick={toggleBotRows} count={rows.length}
              testId="botmining-collapse" />
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="modern" data-testid="botmining-table">
            <thead><tr><th>Wallet</th><th>Level</th><th>Available BOT</th><th>Total BOT</th><th>Static</th><th>Level dividend</th><th>Withdrawn</th><th>Hashrate held</th><th>Hashrate BOT</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan="9" className="text-center py-8 text-ink-3" data-testid="botmining-empty">No BOT mining income in this portfolio yet.</td></tr>
              : !botRowsOpen ? (
                <tr data-testid="botmining-collapsed">
                  <td colSpan="9" className="text-sm text-ink-3 py-6 text-center">
                    {rows.length} wallet{rows.length === 1 ? "" : "s"} hidden — the tiles above already
                    total every one of them.
                  </td>
                </tr>
              ) : botPager.pageItems.map((w) => (
                <tr key={w.node_id || w.address}>
                  <td className="text-sm">{w.name || w.address || "—"}</td>
                  <td className="text-sm">{botLevelLabel(w.bot_level) ? <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-bold" style={{ background: "rgba(21,220,172,0.14)", color: "#0b7a5e" }}>{botLevelLabel(w.bot_level)}</span> : "—"}</td>
                  <td className="num font-semibold" style={{ color: BOT_GREEN }}>{bot4(w.bot_reward)}</td>
                  <td className="num">{bot4(w.bot_reward_total)}</td>
                  <td className="num">{bot4(w.bot_static_total)}</td>
                  <td className="num">{bot4(w.bot_dividend_total)}</td>
                  <td className="num">{bot4(w.bot_withdrawn)}</td>
                  <td className="num" data-testid={`bothash-held-${w.node_id || w.address}`}>{w.bot_power_hashrate ? Number(w.bot_power_hashrate).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}</td>
                  <td className="num font-semibold" style={{ color: HASH_BLUE }} data-testid={`bothash-earned-${w.node_id || w.address}`}>{bot4(w.bot_power_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {botRowsOpen && <TablePager pager={botPager} testId="botmining-pager" label="wallets" />}
      </div>
    </div>
  );
}


export function StakingView({ positions, stakingEntries, botStake, caPrice, botPrice, wallets, holdTotals, snapshotSeries }) {
  const principalCa = positions.reduce((a, p) => a + (p.ca_principal || 0), 0);
  const rewardsCa = positions.reduce((a, p) => a + (p.earned_rewards || 0), 0);
  // Daily-yield estimate uses the CURRENTLY-LOCKED principal (CaryPact "Stake Principal"),
  // not the original — a flexible stake whose principal was reduced no longer earns on the
  // withdrawn portion, so `ca_principal` (original) would overstate the ongoing yield.
  const dailyYieldUsd = estDailyYieldCa(positions) * caPrice;
  const withdrawableCa = positions.reduce((a, p) => a + (p.withdrawable_ca || 0), 0);
  // A row read through someone else's showcase carries its own anonymous label —
  // the numbers are the leader's to share, the wallet names are not.
  const wLabel = (id, row) => row?.wallet_label || wallets.find((w) => w.id === id)?.label || "—";

  // "Principal staked" KPI mirrors CaryPact's "My Staking" (currently locked CA) so
  // the top-line number matches what the user sees on CaryPact. `hold.totals.staked_ca`
  // is a portfolio-scoped sum of `cp_data.totalLockCaAmount` from the daily tree sync.
  // Positions-based principal is kept as a fallback for portfolios/environments that
  // don't have sync data (rare) — same value users saw before this fix.
  const lockedCa = holdTotals && typeof holdTotals.staked_ca === "number" ? holdTotals.staked_ca : principalCa;
  // CA and BOT are kept apart here on purpose: `staked_usd` is the two added up, so
  // the CA box reads `staked_ca_usd` and never quietly carries BOT inside it.
  const lockedUsd = holdTotals && typeof holdTotals.staked_ca_usd === "number" ? holdTotals.staked_ca_usd : lockedCa * caPrice;
  const stakedAllUsd = holdTotals && typeof holdTotals.staked_usd === "number" ? holdTotals.staked_usd : lockedUsd;

  // Historical CA price (on-chain pool) overlaid on the wealth curve so you can see how
  // CA price swings line up with your staking value. Map of date → close price.
  const [priceByDate, setPriceByDate] = useState({});
  const [botByDate, setBotByDate] = useState({});
  const [showPrice, setShowPrice] = useState(false);
  const [showBot, setShowBot] = useState(false);
  const [unit, setUnit] = useState("usd");
  useEffect(() => {
    let live = true;
    api.get("/ca-price/history?range=all")
      .then(({ data }) => {
        if (!live) return;
        const m = {}, mb = {};
        (data.points || []).forEach((p) => {
          if (p.ca != null) m[p.date] = p.ca;
          if (p.bot != null) mb[p.date] = p.bot;
        });
        setPriceByDate(m);
        setBotByDate(mb);
      })
      .catch(() => { /* overlay is optional */ });
    return () => { live = false; };
  }, []);

  // BOT staking (CaryPact's "BOT pledge") is the same product in another asset, so
  // its rewards are ADDED to the staking numbers here rather than given a line of
  // their own.
  const botStakeT = botStake?.totals || null;
  // Dated BOT money for the chart: rewards per day and principal stepping in on each
  // order's stake date. Always in USD — a BOT amount and a CA amount can't be added,
  // so in CA mode it's divided back out at the CA price.
  const botGrowth = useMemo(() => {
    const pos = botStake?.positions || [];
    const inc = botStakingIncomeByDate(botStake?.entries || [], pos, botPrice || 0, todayIn());
    const days = Object.keys(inc.byDate).sort();
    let run = 0;
    const cum = {};
    days.forEach((d) => { run += inc.byDate[d]; cum[d] = run; });
    return { byDate: inc.byDate, cum, days, positions: pos, total: inc.total };
  }, [botStake, botPrice]);

  const botAt = useMemo(() => {
    const { cum, days, positions: pos } = botGrowth;
    return (date) => {
      // Carry the last cumulative reward forward so a day without a record reads as
      // "unchanged" instead of dropping back to zero.
      let i = 0, reward = 0;
      while (i < days.length && days[i] <= date) { reward = cum[days[i]]; i++; }
      return { reward, principal: botStakedOnDate(pos, date) * (botPrice || 0) };
    };
  }, [botGrowth, botPrice]);

  // Staking income chart = money ACTUALLY earned per day (CaryPact reward records
  // when a wallet has been history-imported, otherwise each order's real lifetime
  // rewards spread over the days it was live). No forecast here.
  const income = useMemo(
    () => stakingIncomeByDate(stakingEntries, positions, caPrice, todayIn()),
    [stakingEntries, positions, caPrice],
  );
  const series = useMemo(() => {
    const dates = [...Object.keys(income.byDate), ...Object.keys(botGrowth.byDate)].sort();
    if (!dates.length) return [];
    let start = chartStart(dates[0]);
    const end = todayIn();
    const maxStart = new Date(end + "T00:00:00Z"); maxStart.setUTCDate(maxStart.getUTCDate() - 370);
    if (new Date(start + "T00:00:00Z") < maxStart) start = maxStart.toISOString().slice(0, 10);
    if (start > end) return [];
    // Both assets, same day: the "earned that day" bars have to agree with the
    // wealth curve above them.
    return eachDay(start, end).map((d) => ({
      date: d,
      usd: +((income.byDate[d] || 0) + (botGrowth.byDate[d] || 0)).toFixed(2),
      bot_usd: +(botGrowth.byDate[d] || 0).toFixed(2),
    }));
  }, [income, botGrowth]);

  // "Staking value over time" — three lines, each one a real number:
  //   capital staked  your own stake contracts, so it steps the day you stake
  //   rewards earned   what CaryPact's reward records say those contracts have paid
  //   total value      CaryPact's own My Staking balance, read once a day
  //
  // The old curve DERIVED capital as (balance − rewards) and then stretched it by a
  // reconciliation factor so the three lines would agree. That left the capital line
  // below the money actually staked — "it starts at 34,000, the real number is much
  // higher" — and could slide downhill a little every day. Nothing is reconciled now.
  const synced = useMemo(() => positions.filter((p) => p.imported), [positions]);
  const syncedRewardsCa = synced.reduce((a, p) => a + (p.earned_rewards || 0), 0);
  const currentRewardsUsd = syncedRewardsCa * caPrice;
  const realPoints = useMemo(
    () => (snapshotSeries || []).filter((p) => typeof p.staked_ca === "number" && p.date),
    [snapshotSeries],
  );
  const growthSource = realPoints.length >= 2 ? "snapshots" : "model";

  // The chart opens on the day the FIRST stake was made, not on the first day we
  // ever synced: a portfolio that has been staking since June used to appear out of
  // nowhere in August at whatever it was worth by then, its whole ramp-in missing.
  const curve = useMemo(() => {
    const first = [...synced.map((p) => p.date_staked), realPoints[0]?.date]
      .filter(Boolean).sort()[0];
    if (!first) return [];
    const end = todayIn();
    let start = chartStart(first);
    const maxStart = new Date(end + "T00:00:00Z");
    maxStart.setUTCDate(maxStart.getUTCDate() - 370);
    const capped = maxStart.toISOString().slice(0, 10);
    if (start < capped) start = capped;
    return buildStakingCurve({ positions: synced, entries: stakingEntries, snapshots: realPoints, start, end });
  }, [synced, stakingEntries, realPoints]);

  const growth = useMemo(() => {
    const mul = unit === "ca" ? 1 : caPrice;
    // BOT is held in BOT and valued in dollars, so in CA mode it is divided back out
    // at the CA price — the two assets are never added in their own units.
    const conv = unit === "ca" ? (caPrice > 0 ? 1 / caPrice : 0) : 1;
    let lastPrice = null;
    let lastBot = null;
    return curve.map((r) => {
      if (priceByDate[r.date] != null) lastPrice = priceByDate[r.date];
      if (botByDate[r.date] != null) lastBot = botByDate[r.date];
      // BOT rides on top of the CA lines, as the same two kinds of money.
      const bot = botAt(r.date);
      return {
        date: r.date,
        usd: +(r.rewardCa * mul + bot.reward * conv).toFixed(2),
        principal_usd: +(r.capitalCa * mul + bot.principal * conv).toFixed(2),
        total_value_usd: +(r.totalCa * mul + (bot.principal + bot.reward) * conv).toFixed(2),
        bot_usd: +((bot.principal + bot.reward) * conv).toFixed(2),
        ca_price: lastPrice != null ? +Number(lastPrice).toFixed(2) : null,
        bot_price: lastBot != null ? +Number(lastBot).toFixed(2) : null,
      };
    });
  }, [curve, unit, caPrice, priceByDate, botByDate, botAt]);

  // Today's real figures, straight off the curve. Capital + rewards can sit ABOVE the
  // staking balance, and that gap is real money too: principal a finished term has
  // released, plus rewards already claimed out of staking.
  const nowRow = curve.length ? curve[curve.length - 1] : null;
  const capitalNowCa = nowRow ? nowRow.capitalCa : 0;
  const releasedCa = nowRow ? Math.max(0, nowRow.capitalCa + nowRow.rewardCa - nowRow.totalCa) : 0;
  const releasedWorthSaying = nowRow ? releasedCa > Math.max(1, 0.004 * nowRow.totalCa) : false;
  // Did a flexible account's present-day principal have to be held back to the
  // balance CaryPact actually reported on an earlier day? Worth saying out loud.
  const flexCapped = useMemo(() => curve.some((r) => r.capped), [curve]);

  const [orderQuery, setOrderQuery] = useState("");
  // Rewards-to-capital ratio, day by day. Both sides are valued the same way, so the
  // price cancels out entirely — this is the same number in CA or USD, i.e. pure yield
  // progress. Parity (1.00) is the day your earnings equal the capital you put in.
  const ratioSeries = useMemo(() => growth
    .filter((r) => r.principal_usd > 0 && typeof r.usd === "number")
    .map((r) => ({ date: r.date, ratio: +(r.usd / r.principal_usd).toFixed(5) })),
  [growth]);

  const ratioStats = useMemo(() => {
    if (ratioSeries.length < 3) return null;
    const last = ratioSeries[ratioSeries.length - 1];
    const crossed = ratioSeries.find((r) => r.ratio >= 1);
    // Pace comes from the CONTRACT RATES, not from the ratio's recent slope. The slope
    // is the wrong instrument: staking fresh capital drops the ratio on purpose (new
    // capital hasn't earned yet), so his 40 + 5 CA stakes on 24-25 Sept made a 3-week
    // reading come out NEGATIVE and the crossover unknowable. Rewards per day ÷ capital
    // is the rate the ratio actually climbs at once nothing new is added.
    // Rewards per day ÷ the capital actually staked (the contracts, not a derived
    // figure) is the rate the ratio climbs at once nothing new is added.
    const capitalCa = capitalNowCa > 0 ? capitalNowCa : Math.max(0, lockedCa - syncedRewardsCa);
    const perDay = capitalCa > 0 ? estDailyYieldCa(positions) / capitalCa : 0;
    let parityOn = null;
    let daysToParity = null;
    if (!crossed && perDay > 0.00002) {
      daysToParity = Math.ceil((1 - last.ratio) / perDay);
      const t = new Date(`${last.date}T00:00:00Z`);
      t.setUTCDate(t.getUTCDate() + daysToParity);
      parityOn = t.toISOString().slice(0, 10);
    }
    return { last: last.ratio, crossed: crossed ? crossed.date : null, perDay, parityOn, daysToParity };
  }, [ratioSeries, positions, lockedCa, syncedRewardsCa, capitalNowCa]);

  // The table lists BOTH assets. A BOT pledge order is the same kind of thing as a CA
  // stake — same columns, different token — so it belongs in this list rather than
  // nowhere. Every row carries its own asset and every cell is read in that asset;
  // nothing here is ever added across the two.
  const allPositions = useMemo(() => {
    const ca = positions.map((p) => ({ ...p, asset: "CA" }));
    const bot = (botStake?.positions || []).map((p) => ({
      ...p,
      asset: "BOT",
      ca_principal: p.bot_principal,
      withdrawable_ca: (p.staking_type === "fixed"
        ? (p.released_principal || 0) + (p.pending_reward || 0)
        : (p.current_principal || 0) + (p.pending_reward || 0)),
      daily_rate: null,
    }));
    return [...ca, ...bot];
  }, [positions, botStake]);

  const visiblePositions = useMemo(() => {
    const sorted = [...allPositions].sort((a, b) => (b.date_staked || "").localeCompare(a.date_staked || ""));
    const q = orderQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((p) => String(p.cp_order_no || "").toLowerCase().includes(q));
  }, [allPositions, orderQuery]);
  const stakingPager = usePaged(visiblePositions);

  // Dollar value of a CA amount at the current price — shown on hover for every CA column.
  const caUsd = (n) => (n == null ? undefined : `≈ ${usd(Number(n) * caPrice, 2)} @ $${caPrice.toFixed(2)}/CA`);
  // The same for a row, in whichever asset that row is held.
  const rowPrice = (p) => (p.asset === "BOT" ? (botPrice || 0) : caPrice);
  const rowUsd = (p, n) => (n == null ? undefined
    : `≈ ${usd(Number(n) * rowPrice(p), 2)} @ $${rowPrice(p).toFixed(2)}/${p.asset}`);

  return (
    <div data-testid="staking-view">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 mb-4">
        <MiniKPI label="Staked · My Staking" value={usd(stakedAllUsd)}
          sub={botStakeT && botStakeT.staked_bot > 0
            ? `${lockedCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA + ${botStakeT.staked_bot.toLocaleString(undefined, { maximumFractionDigits: 2 })} BOT`
            : `${lockedCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA`}
          color="#C2185B" tid="staking-kpi-principal" />
        <MiniKPI label="Rewards earned" value={usd(rewardsCa * caPrice + (botStakeT?.earned_usd || 0))}
          sub={botStakeT && botStakeT.earned_bot > 0
            ? `${rewardsCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA + ${botStakeT.earned_bot.toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT`
            : `${rewardsCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA`}
          color="#00863A" tid="staking-kpi-rewards" />
        <MiniKPI label="Withdrawable" value={usd(withdrawableCa * caPrice, 2)} sub={`${withdrawableCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA claimable`} color="#4060FF" tid="staking-kpi-withdrawable" />
        <MiniKPI label="Positions" value={allPositions.length}
          sub={botStakeT && botStakeT.positions > 0 ? `${positions.length} CA + ${botStakeT.positions} BOT` : undefined}
          tid="staking-kpi-positions" />
        <MiniKPI label="Est. forward yield" value={usd(dailyYieldUsd, 2)} sub={`projection · @ CA $${caPrice.toFixed(2)}`} tid="staking-kpi-yield" />
      </div>
      <div className="glass p-5 mb-4" data-testid="staking-growth-card">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(0,134,58,0.12)" }}><TrendingUp size={18} style={{ color: "#00863A" }} /></div>
          <div>
            <div className="kpi-label">{growthSource === "snapshots"
              ? "Wealth curve · real daily readings from CaryPact"
              : "Wealth curve · straight off your stake contracts"}{botGrowth.total > 0 ? " · CA + BOT pledge" : ""}</div>
            <h3 className="font-display text-xl font-semibold tracking-tight">Staking value over time</h3>
          </div>
          <div className="inline-flex rounded-full p-0.5" style={{ background: "rgba(15,18,38,0.05)", border: "1px solid rgba(15,18,38,0.10)" }} data-testid="staking-unit-switch">
            {[["usd", "USD"], ["ca", "CA"]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setUnit(key)}
                aria-pressed={unit === key}
                data-testid={`staking-unit-${key}`}
                className="px-2.5 py-1 rounded-full text-[11px] font-bold transition-colors"
                style={unit === key
                  ? { background: "#fff", color: "#0F1226", boxShadow: "0 1px 2px rgba(15,18,38,0.12)" }
                  : { color: "#64748B" }}
                title={key === "ca"
                  ? "Show CA amounts — takes the price swing out, so you see the yield on its own"
                  : "Show dollar values at today's CA price"}
              >
                {label}
              </button>
            ))}
          </div>
          {unit === "usd" && growthSource === "snapshots" && botStakeT && (botStakeT.staked_bot > 0 || botStakeT.positions > 0) && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold"
              data-testid="staking-bot-chip"
              style={{ background: "rgba(14,116,144,0.10)", color: "#0E7490", border: "1px solid rgba(14,116,144,0.28)" }}
              title="CaryPact BOT pledge — included in the staking totals and on the staking line">
              <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#0E7490" }} />
              + {botStakeT.staked_bot.toLocaleString(undefined, { maximumFractionDigits: 2 })} BOT staked
            </span>
          )}
          <button
            type="button"
            onClick={() => setShowPrice((v) => !v)}
            aria-pressed={showPrice}
            data-testid="toggle-ca-price-overlay"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
            style={showPrice
              ? { background: "rgba(141,28,235,0.14)", color: "#7C1DD1", border: "1px solid rgba(141,28,235,0.35)" }
              : { background: "rgba(15,18,38,0.05)", color: "#64748B", border: "1px solid rgba(15,18,38,0.10)" }}
            title={showPrice ? "Hide the CA price overlay" : "Show the CA price overlay"}
          >
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: showPrice ? "#8D1CEB" : "#94A3B8" }} />
            CA price {showPrice ? "on" : "off"}
          </button>
          <button
            type="button"
            onClick={() => setShowBot((v) => !v)}
            aria-pressed={showBot}
            data-testid="toggle-bot-price-overlay"
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[11px] font-semibold transition-colors"
            style={showBot
              ? { background: "rgba(37,99,235,0.14)", color: "#1D4ED8", border: "1px solid rgba(37,99,235,0.35)" }
              : { background: "rgba(15,18,38,0.05)", color: "#64748B", border: "1px solid rgba(15,18,38,0.10)" }}
            title={showBot ? "Hide the BOT price overlay" : "Show the BOT price overlay"}
          >
            <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: showBot ? "#2563EB" : "#94A3B8" }} />
            BOT price {showBot ? "on" : "off"}
          </button>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            {/* Four boxes, each one asset and one kind of money: what is staked and
                what it has earned, BOT then CA. Nothing combined, nothing implied. */}
            {botStakeT && (botStakeT.staked_bot > 0 || botStakeT.positions > 0) && (
              <>
                <div className="kpi-tile !p-3" data-testid="kpi-bot-staking"
                  title={`${botStakeT.staked_bot} BOT staked in CaryPact's BOT pledge across ${botStakeT.positions || botStakeT.summary_wallets} ${botStakeT.positions ? "order" : "wallet"}${(botStakeT.positions || botStakeT.summary_wallets) === 1 ? "" : "s"}, valued at the BOT price. ${botStakeT.source === "orders" ? "Read from the orders themselves." : "Read from each wallet's CaryPact summary — the per-order detail needs one Wallet sweep pass."}`}>
                  <div className="kpi-label flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#0E7490" }} />BOT staking</div>
                  <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#0E7490" }}>${(botStakeT.staked_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
                  <div className="text-[10px] text-ink-3 mt-0.5">{botStakeT.staked_bot.toLocaleString(undefined, { maximumFractionDigits: 4 })} BOT @ ${(botStake?.bot_price || 0).toFixed(2)}</div>
                </div>
                <div className="kpi-tile !p-3" data-testid="kpi-bot-staking-rewards"
                  title={botStakeT.rewards_read
                    ? `${botStakeT.earned_bot} BOT earned by the BOT pledge${botStakeT.reward_days ? ` across ${botStakeT.reward_days} days of records` : ""}${botStakeT.pending_bot ? `, with ${botStakeT.pending_bot} BOT pending` : ""}. Separate from the BOT staked.`
                    : "CaryPact's daily BOT reward records need one Wallet sweep pass before they can be read."}>
                  <div className="kpi-label flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#0F766E" }} />BOT rewards earned {!botStakeT.rewards_read && <span className="text-[9px] text-ink-3 font-normal">not read</span>}</div>
                  <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#0F766E" }}>{botStakeT.rewards_read ? `+$${(botStakeT.earned_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</div>
                  <div className="text-[10px] text-ink-3 mt-0.5">{botStakeT.rewards_read
                    ? `${botStakeT.earned_bot.toLocaleString(undefined, { maximumFractionDigits: 6 })} BOT · separate from BOT staked`
                    : "needs one Wallet sweep pass"}</div>
                </div>
              </>
            )}
            <div className="kpi-tile !p-3" style={{ background: "linear-gradient(135deg, rgba(29,78,216,0.10), rgba(194,24,91,0.08))" }} data-testid="kpi-staking-principal">
              <div className="kpi-label flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#1D4ED8" }} />CA staking · CaryPact</div>
              <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#1D4ED8" }}>${lockedUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-ink-3 mt-0.5">{lockedCa.toLocaleString(undefined, { maximumFractionDigits: 2 })} CA @ ${caPrice.toFixed(2)}</div>
            </div>
            <div className="kpi-tile !p-3" data-testid="kpi-staking-rewards">
              <div className="kpi-label flex items-center gap-1.5"><span className="inline-block w-2 h-2 rounded-full" style={{ background: "#00863A" }} />CA rewards earned <span className="text-[9px] text-ink-3 font-normal">est.</span></div>
              <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#00863A" }}>+${currentRewardsUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-ink-3 mt-0.5">{syncedRewardsCa.toFixed(2)} CA · separate from CA staked</div>
            </div>
          </div>
        </div>
        {growth.length === 0 ? (
          <div className="text-sm text-ink-3 py-12 text-center" data-testid="staking-growth-empty">No synced staking positions yet — connect a wallet in "Update data" to import them from CaryPact.</div>
        ) : (
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={growth} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="stakePrincipalFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4060FF" stopOpacity={0.30} />
                    <stop offset="100%" stopColor="#4060FF" stopOpacity={0.02} />
                  </linearGradient>
                  <linearGradient id="stakeGrowthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#00863A" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="#00863A" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis yAxisId="left" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => (unit === "ca" ? `${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })} CA` : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`)} width={unit === "ca" ? 74 : 60} />
                {(showPrice || showBot) && (
                  <YAxis yAxisId="price" orientation="right" stroke={showPrice && showBot ? "#6B7280" : showBot ? "#2563EB" : "#8D1CEB"} fontSize={11} tickLine={false} axisLine={false} width={46} domain={["auto", "auto"]} tickFormatter={(v) => `$${Number(v).toFixed(0)}`} />
                )}
                <Tooltip
                  contentStyle={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)" }}
                  formatter={(v, name) => {
                    const amt = (n, dp = 0) => (unit === "ca"
                      ? `${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })} CA`
                      : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: dp })}`);
                    if (name.startsWith("Capital staked")) return [amt(v), "Capital staked"];
                    if (name.startsWith("Rewards earned")) return [`+${amt(v, 2)}`, "Rewards earned"];
                    if (name.startsWith("Total value")) return [amt(v), "Total value"];
                    if (name.startsWith("of which BOT")) return [amt(v, 2), "of which BOT pledge"];
                    if (name === "CA price (USD)") return [`$${Number(v).toFixed(2)}`, "CA price"];
                    if (name === "BOT price (USD)") return [`$${Number(v).toFixed(2)}`, "BOT price"];
                    return [amt(v), name];
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area yAxisId="left" type="monotone" dataKey="principal_usd" name={`Capital staked (${unit === "ca" ? "CA" : "USD"})`} stroke="#4060FF" strokeWidth={2.5} fill="url(#stakePrincipalFill)" dot={false} isAnimationActive animationDuration={700} />
                <Area yAxisId="left" type="monotone" dataKey="usd" name={`Rewards earned (${unit === "ca" ? "CA" : "USD"})`} stroke="#00863A" strokeWidth={2.5} fill="url(#stakeGrowthFill)" dot={false} isAnimationActive animationDuration={900} />
                <Line yAxisId="left" type="monotone" dataKey="total_value_usd" name={`Total value (${unit === "ca" ? "CA" : "USD"})`} stroke="#7C3AED" strokeWidth={2} strokeDasharray="5 4" fill="none" dot={false} isAnimationActive animationDuration={900} />
                {showPrice && (
                  <Line yAxisId="price" type="monotone" dataKey="ca_price" name="CA price (USD)" stroke="#8D1CEB" strokeWidth={1.75} dot={false} connectNulls isAnimationActive animationDuration={900} />
                )}
                {showBot && (
                  <Line yAxisId="price" type="monotone" dataKey="bot_price" name="BOT price (USD)" stroke="#2563EB" strokeWidth={1.75} dot={false} connectNulls isAnimationActive animationDuration={900} />
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
        <div className="text-[11px] text-ink-3 mt-3 flex items-center gap-3 flex-wrap" data-testid="staking-growth-source">
          <span className="inline-flex items-center gap-1.5"><span style={{ display: "inline-block", width: 14, height: 4, background: "rgba(64,96,255,0.55)" }} /> capital staked</span>
          <span className="inline-flex items-center gap-1.5"><span style={{ display: "inline-block", width: 14, height: 4, background: "rgba(0,134,58,0.65)" }} /> rewards earned</span>
          <span className="inline-flex items-center gap-1.5"><span style={{ display: "inline-block", width: 14, height: 0, borderTop: "2px dashed #7C3AED" }} /> total value</span>
          {showPrice && (
            <span className="inline-flex items-center gap-1.5"><span style={{ display: "inline-block", width: 14, height: 3, background: "#8D1CEB" }} /> CA price · right axis</span>
          )}
          {showBot && (
            <span className="inline-flex items-center gap-1.5"><span style={{ display: "inline-block", width: 14, height: 3, background: "#2563EB" }} /> BOT price · right axis</span>
          )}
          {growthSource === "snapshots" ? (
            <span className="opacity-70">
              · <b>Capital staked</b> is your own stake contracts added up — {ca2(capitalNowCa)} CA
              across {synced.length} order{synced.length === 1 ? "" : "s"} today, the same amounts
              listed below — so it steps the day you stake and holds flat in between, and the figure
              is never scaled to fit the other lines. <b>Rewards earned</b> is what CaryPact&apos;s
              reward records say those contracts have paid. <b>Total value</b> is CaryPact&apos;s own
              My Staking balance, read once a day ({realPoints.length} sync
              {realPoints.length === 1 ? " day" : " days"} from {realPoints[0].date}; a flat stretch
              means no sync that day) — before that first reading it is capital plus the rewards the
              contracts had earned by then.
              {flexCapped
                ? " One of these is a flexible stake, and CaryPact keeps a flexible account as a single order whose principal grows with every top-up and every compounded reward — so its amount is today's figure on the day the account opened. On the earlier days it is held to the balance CaryPact actually reported instead of being back-dated as a lump, which is why the capital line tracks the balance there rather than jumping."
                : ""}
              {releasedWorthSaying
                ? ` Capital and rewards together sit ${ca2(releasedCa)} CA above the balance: that much principal has been released by finished terms, or claimed out of staking.`
                : ""}
              {unit === "usd"
                ? " Every line is priced at today's CA, so a falling CA price pulls them all down even though your CA never moved — switch to CA to see the yield on its own."
                : " CA amounts, so the price swing is out of the picture entirely."}
              {unit === "usd" ? ` CA $${caPrice.toFixed(2)}.` : ""}
              {unit === "usd" && botStakeT && (botStakeT.staked_bot > 0 || botStakeT.positions > 0)
                ? ` BOT staking is in these numbers too: ${botStakeT.staked_bot} BOT staked${botStakeT.rewards_read ? ` and ${botStakeT.earned_bot} BOT earned` : ", with its daily rewards still to be pulled from CaryPact"}, valued at the BOT price.`
                : ""}
            </span>
          ) : (
            <span className="opacity-70">
              · No daily sync readings for this portfolio yet, so <b>total value</b> is your capital
              plus the rewards your contracts have earned — both read straight off the orders. Run a
              sync and CaryPact&apos;s own My Staking balance takes that line over.
              CA ${caPrice.toFixed(2)}.
            </span>
          )}
        </div>
      </div>

      {ratioStats && (
        <div className="glass p-5 mb-4" data-testid="staking-ratio-card">
          <div className="flex items-start gap-3 mb-4 flex-wrap">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "rgba(124,58,237,0.12)" }}><Gauge size={18} style={{ color: "#7C3AED" }} /></div>
            <div>
              <div className="kpi-label">Rewards against capital · the crossover</div>
              <h3 className="font-display text-xl font-semibold tracking-tight">Reward to capital ratio</h3>
            </div>
            <div className="ml-auto flex items-center gap-2 flex-wrap">
              <div className="kpi-tile !p-3" data-testid="staking-ratio-now">
                <div className="kpi-label">Today</div>
                <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#7C3AED" }}>
                  1 : {(1 / ratioStats.last).toFixed(1)}
                </div>
                <div className="text-[10px] text-ink-3 mt-0.5">{(ratioStats.last * 100).toFixed(1)}% of capital earned back</div>
              </div>
              <div className="kpi-tile !p-3" data-testid="staking-ratio-pace">
                <div className="kpi-label">Pace</div>
                <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: "#00863A" }}>
                  +{(ratioStats.perDay * 100).toFixed(2)}%<span className="text-[11px] font-normal">/day</span>
                </div>
                <div className="text-[10px] text-ink-3 mt-0.5">at your contract rates</div>
              </div>
              <div className="kpi-tile !p-3" data-testid="staking-ratio-parity">
                <div className="kpi-label">Rewards overtake capital</div>
                <div className="num font-semibold" style={{ fontSize: 18, lineHeight: 1.1, color: ratioStats.crossed ? "#00863A" : "#C2185B" }}>
                  {ratioStats.crossed ? "Done" : ratioStats.parityOn ? ratioStats.parityOn : "—"}
                </div>
                <div className="text-[10px] text-ink-3 mt-0.5">
                  {ratioStats.crossed ? `crossed ${ratioStats.crossed}`
                    : ratioStats.daysToParity ? `about ${ratioStats.daysToParity} days at this pace`
                      : "not moving yet"}
                </div>
              </div>
            </div>
          </div>
          <div style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={ratioSeries} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="stakeRatioFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7C3AED" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#7C3AED" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} width={54}
                  domain={[0, (max) => Math.max(1.05, max * 1.15)]}
                  tickFormatter={(v) => `${Number(v).toFixed(2)}×`} />
                <Tooltip
                  contentStyle={{ border: "1px solid rgba(15,18,38,0.08)", borderRadius: 12, background: "rgba(255,255,255,0.95)", backdropFilter: "blur(20px)" }}
                  formatter={(v) => [`${Number(v).toFixed(3)}× · 1 : ${(1 / Number(v)).toFixed(1)}`, "Rewards per unit of capital"]}
                />
                <ReferenceLine y={1} stroke="#C2185B" strokeDasharray="4 4" strokeWidth={1.5}
                  label={{ value: "parity — rewards = capital", position: "insideTopRight", fill: "#C2185B", fontSize: 11 }} />
                <Area type="monotone" dataKey="ratio" name="Rewards per unit of capital" stroke="#7C3AED" strokeWidth={2.5} fill="url(#stakeRatioFill)" dot={false} isAnimationActive animationDuration={800} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="text-[11px] text-ink-3 mt-3" data-testid="staking-ratio-note">
            Rewards earned for every unit of capital staked, day by day. Price drops out — both sides are valued the
            same way — so this is yield alone. <b>Parity</b> is the day your earnings match the capital you put in;
            past it, rewards are the bigger half. A fresh stake pushes the ratio back down on purpose, because new
            capital hasn&apos;t earned yet — which is why the crossover date is worked out from your contracts&apos;
            daily rates against today&apos;s capital, not from the line&apos;s recent slope. Restake and the date
            moves out; leave it be and it pulls in.
          </div>
        </div>
      )}
      <div className="glass p-5 mb-4" data-testid="staking-chart">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(194,24,91,0.12)" }}><Coins size={16} style={{ color: "#C2185B" }} /></div>
          <h3 className="font-display text-lg font-semibold tracking-tight">Staking rewards earned per day</h3>
        </div>
        <div className="text-[11px] text-ink-3 -mt-2 mb-3" data-testid="staking-chart-source">
          {income.source === "imported"
            ? "Real CaryPact reward records — the CA actually credited each day."
            : income.source === "mixed"
              ? "Real CaryPact reward records for the days and wallets it hands them over for; every other day keeps the rest of that order's real earned rewards, spread across the days it was live. Totals are real money either way."
              : "Built from each order's real earned rewards, spread across the days it was live (the totals are real money, the daily shape is derived). Sync a wallet with a fresh CaryPact token to pull the exact per-day records."}
        </div>
        {series.length === 0 ? (
          <div className="text-sm text-ink-3 py-12 text-center" data-testid="staking-empty">No staking rewards yet — connect a wallet in "Update data" to import them from CaryPact.</div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs><linearGradient id="gStk" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#C2185B" stopOpacity={0.4} /><stop offset="100%" stopColor="#C2185B" stopOpacity={0.03} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [usd(v, 2), "Earned that day"]} />
                <Area type="monotone" dataKey="usd" name="Earned that day" stroke="#C2185B" strokeWidth={2} fill="url(#gStk)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="glass overflow-hidden">
        <div className="p-5 border-b border-black/5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-display text-lg font-semibold tracking-tight">Staking positions</h3>
              <p className="text-xs text-ink-3 mt-1" data-testid="staking-source-note">
                Positions are imported per <b>connected</b> wallet (Update data → CaryPact history). A wallet in this portfolio
                without its own connection can&apos;t share its staking orders — connect it, then run &quot;Import history&quot;.
                {botStakeT && botStakeT.positions > 0
                  ? <> BOT pledge orders sit in the same list, tagged <b>BOT</b> — every figure in those rows is BOT, never CA.</>
                  : null}
              </p>
            </div>
            <div className="relative shrink-0" style={{ minWidth: 200 }}>
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3" />
              <input
                type="text"
                value={orderQuery}
                onChange={(e) => setOrderQuery(e.target.value)}
                placeholder="Search order #…"
                data-testid="staking-order-search"
                className="w-full pl-8 pr-8 py-1.5 text-sm rounded-lg border border-black/10 bg-white/70 outline-none focus:border-black/25 transition-colors"
              />
              {orderQuery && (
                <button
                  onClick={() => setOrderQuery("")}
                  data-testid="staking-order-search-clear"
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink"
                  aria-label="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="modern" data-testid="staking-table">
            <thead><tr><th>Date staked</th><th>Asset</th><th>Wallet</th><th>Order #</th><th>Type</th><th>Term</th><th title="Amount originally staked (constant)">Initial Principal</th><th title="Still-locked balance now — CaryPact 'Stake Principal'">Stake Principal</th><th title="Principal already released back to your wallet (fixed stakes drip daily)">Released</th><th>Earned Rewards</th><th title="Stake principal + released + pending rewards">Total Compound</th><th title="Claimable now — (fixed: released + pending rewards; flexible: stake principal + pending rewards)">Withdrawable</th><th>Rewards $</th><th>Rate / day</th><th>Status</th><th>Source</th></tr></thead>
            <tbody>
              {allPositions.length === 0 ? <tr><td colSpan="16" className="text-center py-8 text-ink-3">Nothing yet.</td></tr>
              : visiblePositions.length === 0 ? <tr><td colSpan="16" className="text-center py-8 text-ink-3" data-testid="staking-no-match">No positions match "{orderQuery}".</td></tr>
              : stakingPager.pageItems.map((p) => (
                <tr key={p.id} data-testid={`staking-row-${p.id}`}>
                  <td className="num text-xs">{p.date_staked}</td>
                  <td>
                    <span className="chip text-[10px] font-semibold" data-testid={`staking-asset-${p.id}`}
                      style={p.asset === "BOT"
                        ? { background: "rgba(14,116,144,0.12)", color: "#0E7490" }
                        : { background: "rgba(29,78,216,0.10)", color: "#1D4ED8" }}
                      title={p.asset === "BOT" ? "CaryPact BOT pledge — every figure in this row is BOT" : "CaryPact CA staking — every figure in this row is CA"}>
                      {p.asset}
                    </span>
                  </td>
                  <td className="text-sm">{wLabel(p.wallet_id, p)}</td>
                  <td className="num text-xs" title={p.cp_order_no ? `CaryPact order ${p.cp_order_no}` : "Manual entry — no CaryPact order"}>{p.cp_order_no ? `…${String(p.cp_order_no).slice(-6)}` : "—"}</td>
                  <td className="text-xs capitalize">
                    {p.staking_type}
                    {(p.switch_in_ca || 0) > 0 && (
                      <div className="text-[9px] mt-0.5 font-semibold" style={{ color: "#7C3AED" }} data-testid={`staking-switch-note-${p.id}`} title={`Funded by a 'Switch to Long-Term Stake' — ${ca2(p.switch_in_ca)} CA moved from a flexible position (not new capital).`}>↳ from Flexible</div>
                    )}
                  </td>
                  <td className="num text-xs">{p.term_days ? `${p.term_days}d` : "Flex"}</td>
                  <td className="num" data-testid={`staking-initial-${p.id}`} title={rowUsd(p, p.ca_principal)}>{ca2(p.ca_principal)}</td>
                  <td className="num" data-testid={`staking-current-${p.id}`} title={rowUsd(p, p.current_principal)}>{ca2(p.current_principal)}</td>
                  <td className="num text-xs" title={rowUsd(p, p.released_principal)}>{ca2(p.released_principal)}</td>
                  <td className="num" title={rowUsd(p, p.earned_rewards)}>{ca2(p.earned_rewards)}</td>
                  <td className="num text-xs" title={rowUsd(p, p.compound_total)}>{ca2(p.compound_total)}</td>
                  <td className="num font-semibold" style={{ color: "#4060FF" }} data-testid={`staking-withdrawable-${p.id}`} title={rowUsd(p, p.withdrawable_ca)}>{ca2(p.withdrawable_ca)}</td>
                  <td className="num font-semibold" style={{ color: "#00863A" }}>{usd((p.earned_rewards || 0) * rowPrice(p), 2)}</td>
                  <td className="num text-xs">{p.daily_rate == null ? "—" : `${((p.daily_rate || 0) * 100).toFixed(3)}%`}</td>
                  <td>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="chip chip-green text-[10px]">{p.status || "Active"}</span>
                      {p.staking_type === "fixed"
                        ? <span className="chip text-[10px]" style={{ background: "rgba(180,83,9,0.12)", color: "#B45309" }} data-testid={`staking-action-${p.id}`} title="On CaryPact this fixed stake offers a 'Restake' option (roll released principal into a new term).">Restake</span>
                        : <span className="chip text-[10px]" style={{ background: "rgba(64,96,255,0.12)", color: "#4060FF" }} data-testid={`staking-action-${p.id}`} title="On CaryPact this flexible stake offers 'Switch to Long-Term Stake' (move CA into a fixed term).">Switch</span>}
                    </div>
                  </td>
                  <td><SourceChip imported={p.imported} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager pager={stakingPager} testId="staking-pager" label="positions" />
      </div>
    </div>
  );
}

const COMMUNITY_COLORS = ["#7C3AED", "#4060FF", "#C2185B", "#B45309", "#00863A", "#0891B2"];

export function CommunityView({ rows, rewardSeries }) {
  const total = rows.reduce((a, e) => a + (e.usd_value || 0), 0);
  const commRows = useMemo(() => [...rows].sort((a, b) => (b.date || "").localeCompare(a.date || "")), [rows]);
  const commPager = usePaged(commRows);
  const bySource = useMemo(() => {
    const m = {};
    rows.forEach((e) => { const k = e.source || "Other"; m[k] = (m[k] || 0) + (e.usd_value || 0); });
    return Object.entries(m).map(([source, v]) => ({ source, usd: +v.toFixed(2) })).sort((a, b) => b.usd - a.usd);
  }, [rows]);
  const byDate = useMemo(() => {
    const m = {};
    rows.forEach((e) => { m[e.date] = (m[e.date] || 0) + (e.usd_value || 0); });
    // Today is always a partial day — stop the chart at yesterday to avoid a fake drop-off.
    const today = todayIn();
    return Object.entries(m).map(([date, v]) => ({ date, usd: +v.toFixed(2) }))
      .filter((d) => d.date < today)
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [rows]);
  const days = byDate.length;
  const avg = days ? total / days : 0;
  const best = byDate.reduce((m, d) => (d.usd > (m?.usd || -1) ? d : m), null);
  // KPIs count every day; the chart opens on 20 May (see lib/chartWindow).
  // VIP Hashrate Referral settles in USDT on its own CaryPact screen — it belongs
  // to this family, so it rides on the same chart instead of only the tile.
  const chartData = useMemo(() => {
    const vip = {};
    (rewardSeries || []).forEach((r) => { if (r.vip_referral_usd) vip[r.date] = r.vip_referral_usd; });
    const dates = [...new Set([...byDate.map((d) => d.date), ...Object.keys(vip)])].sort();
    const team = {}; byDate.forEach((d) => { team[d.date] = d.usd; });
    return sinceChartStart(dates.map((date) => ({ date, usd: +(team[date] || 0).toFixed(2), vip: +(vip[date] || 0).toFixed(2) })));
  }, [byDate, rewardSeries]);

  return (
    <div data-testid="community-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 mb-4">
        <MiniKPI label="Total team rewards" value={usd(total)} color="#7C3AED" tid="community-kpi-total" />
        <MiniKPI label="Avg / day" value={usd(avg, 2)} tid="community-kpi-avg" />
        <MiniKPI label={best ? `Best · ${best.date}` : "Best day"} value={best ? usd(best.usd, 2) : "—"} color="#B45309" tid="community-kpi-best" />
        <MiniKPI label="Top source" value={bySource[0]?.source || "—"} sub={bySource[0] ? usd(bySource[0].usd) : ""} tid="community-kpi-top" />
      </div>
      {bySource.length > 0 && (
        <div className="glass p-3 mb-4 flex flex-wrap gap-x-4 gap-y-1.5" data-testid="community-sources">
          {bySource.map((s, i) => (
            <div key={s.source} className="flex items-center gap-1.5 text-xs">
              <span className="inline-block w-2 h-2 rounded-full" style={{ background: COMMUNITY_COLORS[i % COMMUNITY_COLORS.length] }} />
              <span className="text-ink-2">{s.source}</span>
              <span className="num font-semibold">{usd(s.usd)}</span>
              <span className="text-ink-3">({total > 0 ? ((s.usd / total) * 100).toFixed(1) : 0}%)</span>
            </div>
          ))}
        </div>
      )}
      <div className="glass p-5 mb-4" data-testid="community-chart">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(124,58,237,0.12)" }}><Users size={16} style={{ color: "#7C3AED" }} /></div>
          <h3 className="font-display text-lg font-semibold tracking-tight">Daily team rewards</h3>
          {best && <span className="ml-auto chip text-[11px]" style={{ background: "rgba(180,83,9,0.1)", color: "#B45309" }}><Trophy size={10} /> Peak {usd(best.usd)} · {best.date}</span>}
        </div>
        {chartData.length === 0 ? (
          <div className="text-sm text-ink-3 py-12 text-center" data-testid="community-empty">No community income yet — connect a wallet in "Update data" to import it from CaryPact.</div>
        ) : (
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                <defs><linearGradient id="gCom" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#7C3AED" stopOpacity={0.4} /><stop offset="100%" stopColor="#7C3AED" stopOpacity={0.03} /></linearGradient></defs>
                <CartesianGrid stroke="rgba(15,18,38,0.06)" vertical={false} />
                <XAxis dataKey="date" stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} minTickGap={28} />
                <YAxis stroke="#6B7280" fontSize={11} tickLine={false} axisLine={false} tickFormatter={(v) => `$${Number(v).toLocaleString()}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [usd(v, 2), n]} />
                <Legend wrapperStyle={{ fontSize: 11 }} iconSize={8} />
                <Area type="monotone" dataKey="usd" name="Team rewards $" stackId="c" stroke="#7C3AED" strokeWidth={2} fill="url(#gCom)" />
                <Area type="monotone" dataKey="vip" name="VIP hashrate referral $" stackId="c" stroke="#B45309" strokeWidth={2} fill="rgba(180,83,9,0.18)" />
                {chartData.length > 45 && <Brush dataKey="date" height={18} stroke="rgba(124,58,237,0.45)" travellerWidth={8} fill="rgba(124,58,237,0.04)" tickFormatter={() => ""} />}
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
      <div className="glass overflow-hidden">
        <div className="p-5 border-b border-black/5"><h3 className="font-display text-lg font-semibold tracking-tight">Recent team reward entries</h3></div>
        <div className="overflow-x-auto">
          <table className="modern" data-testid="community-table">
            <thead><tr><th>Date</th><th>Source</th><th>CA</th><th>USD</th><th>From</th></tr></thead>
            <tbody>
              {rows.length === 0 ? <tr><td colSpan="5" className="text-center py-8 text-ink-3">Nothing yet.</td></tr>
              : commPager.pageItems.map((e) => (
                <tr key={e.id}>
                  <td className="num text-xs">{e.date}</td>
                  <td><span className="chip text-[11px]" style={{ background: "rgba(124,58,237,0.1)", color: "#7C3AED" }}>{e.source || "Other"}</span></td>
                  <td className="num">{e.ca_amount != null ? Number(e.ca_amount).toFixed(4) : "—"}</td>
                  <td className="num font-semibold" style={{ color: "#00863A" }}>${(e.usd_value || 0).toFixed(2)}</td>
                  <td className="text-xs text-ink-3">{e.imported ? "CaryPact import" : (e.from_member || "Manual")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TablePager pager={commPager} testId="community-pager" label="entries" />
      </div>
    </div>
  );
}
